import {
  COLLECTION_EXPORT_FORMAT,
  COLLECTION_IMPORT_SUPPORTED_VERSIONS,
  COLLECTION_IMPORT_FILE_MAX_BYTES,
  COLLECTION_IMPORT_ITEMS_MAX,
  COLLECTION_QUANTITY_MAX,
} from "./constants";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type ValidatedImportItem = {
  releaseId: string;
  quantity: number;
};

export type ImportValidationSuccess = {
  ok: true;
  items: ValidatedImportItem[];
  itemCount: number;
  totalQuantity: number;
};

export type ImportValidationFailure = {
  ok: false;
  message: string;
};

export type ImportValidationResult =
  | ImportValidationSuccess
  | ImportValidationFailure;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Structural client-side validation for collection import JSON.
 * Fail-closed: rejects unknown versions, duplicates, and oversized payloads.
 */
export function validateCollectionImportJson(
  text: string,
  options?: { maxBytes?: number },
): ImportValidationResult {
  const maxBytes = options?.maxBytes ?? COLLECTION_IMPORT_FILE_MAX_BYTES;
  const byteLength = new TextEncoder().encode(text).length;
  if (byteLength > maxBytes) {
    return {
      ok: false,
      message: `Файл слишком большой (максимум ${Math.floor(maxBytes / (1024 * 1024))} МБ).`,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, message: "Файл не является корректным JSON." };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, message: "Корневой элемент должен быть объектом." };
  }

  if (parsed.format !== COLLECTION_EXPORT_FORMAT) {
    return {
      ok: false,
      message: 'Неизвестный формат. Ожидается "hot-wheels-collection".',
    };
  }

  if (!COLLECTION_IMPORT_SUPPORTED_VERSIONS.includes(parsed.version as 1 | 2)) {
    return {
      ok: false,
      message: `Неподдерживаемая версия формата (${String(parsed.version)}).`,
    };
  }

  if (!Array.isArray(parsed.items)) {
    return { ok: false, message: "Поле items должно быть массивом." };
  }

  if (parsed.items.length > COLLECTION_IMPORT_ITEMS_MAX) {
    return {
      ok: false,
      message: `Слишком много записей (максимум ${COLLECTION_IMPORT_ITEMS_MAX}).`,
    };
  }

  const items: ValidatedImportItem[] = [];
  const seen = new Set<string>();

  for (let index = 0; index < parsed.items.length; index += 1) {
    const raw = parsed.items[index];
    if (!isPlainObject(raw)) {
      return {
        ok: false,
        message: `Запись #${index + 1} должна быть объектом.`,
      };
    }

    const { releaseId, quantity } = raw;
    if (typeof releaseId !== "string" || !UUID_RE.test(releaseId)) {
      return {
        ok: false,
        message: `Запись #${index + 1}: некорректный releaseId.`,
      };
    }
    if (seen.has(releaseId)) {
      return {
        ok: false,
        message: "Файл содержит повторяющиеся releaseId.",
      };
    }
    seen.add(releaseId);

    if (
      typeof quantity !== "number" ||
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > COLLECTION_QUANTITY_MAX
    ) {
      return {
        ok: false,
        message: `Запись #${index + 1}: quantity должно быть целым от 1 до ${COLLECTION_QUANTITY_MAX}.`,
      };
    }

    items.push({ releaseId, quantity });
  }

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  return {
    ok: true,
    items,
    itemCount: items.length,
    totalQuantity,
  };
}

export async function readImportFile(file: File): Promise<string> {
  if (file.size > COLLECTION_IMPORT_FILE_MAX_BYTES) {
    throw new Error(
      `Файл слишком большой (максимум ${Math.floor(COLLECTION_IMPORT_FILE_MAX_BYTES / (1024 * 1024))} МБ).`,
    );
  }
  if (typeof file.text === "function") {
    return file.text();
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл."));
    reader.readAsText(file);
  });
}
